/**
 * shared/schema/trust-center.ts
 *
 * Task #1699 — Partner Trust Center (Aggregated Public Profile).
 *
 * Tables:
 *   partner_trust_centers        — one per partner; holds slug, branding,
 *                                   SA enablement, owner publish state.
 *   partner_trust_center_blocks  — ordered block rows that compose the
 *                                   public page (hero, stats, certs, …).
 *   partner_trust_center_versions — append-only publish snapshots so the
 *                                    public page can be diffed / rolled back.
 *   partner_trust_center_views   — lightweight page-view telemetry.
 */

import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
  integer,
  boolean,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

import { tenants, users } from "./common";
import { partners } from "./partner";

export const TRUST_CENTER_STATUSES = [
  "draft",
  "published",
  "paused",
] as const;
export type TrustCenterStatus = (typeof TRUST_CENTER_STATUSES)[number];

export const TRUST_CENTER_BLOCK_TYPES = [
  "hero",
  "about",
  "workforce_stats",
  "certifications",
  "compliance",
  "safety_stats",
  "share_spaces",
  "contact_cta",
  "one_pager",
] as const;
export type TrustCenterBlockType = (typeof TRUST_CENTER_BLOCK_TYPES)[number];

export const trustCenterBrandingSchema = z.object({
  logoUrl: z.string().max(2048).nullable().optional(),
  heroImageUrl: z.string().max(2048).nullable().optional(),
  accentColor: z.string().max(20).nullable().optional(),
  tagline: z.string().max(200).nullable().optional(),
});
export type TrustCenterBranding = z.infer<typeof trustCenterBrandingSchema>;

export const trustCenterSaSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  allowedBlocks: z.array(z.enum(TRUST_CENTER_BLOCK_TYPES)).optional(),
  allowedStats: z.array(z.string().max(80)).optional(),
  noindex: z.boolean().default(true),
  defaultDisclosureLanguage: z.string().max(4000).nullable().optional(),
});
export type TrustCenterSaSettings = z.infer<typeof trustCenterSaSettingsSchema>;

export const DEFAULT_SA_SETTINGS: TrustCenterSaSettings = {
  enabled: false,
  allowedBlocks: [...TRUST_CENTER_BLOCK_TYPES],
  allowedStats: [
    "verified_workers",
    "trades",
    "regions",
    "certifications",
    "incident_free_days",
  ],
  noindex: true,
};

export const trustCenterBlockContentSchema = z.object({
  aboutText: z.string().max(4000).nullable().optional(),
  contactEmail: z.string().email().max(320).nullable().optional(),
  contactPhone: z.string().max(40).nullable().optional(),
  contactUrl: z.string().max(2048).nullable().optional(),
  onePagerUrl: z.string().max(2048).nullable().optional(),
  customLabel: z.string().max(200).nullable().optional(),
});
export type TrustCenterBlockContent = z.infer<
  typeof trustCenterBlockContentSchema
>;

export const partnerTrustCenters = pgTable(
  "partner_trust_centers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    partnerId: text("partner_id")
      .notNull()
      .references(() => partners.id, { onDelete: "cascade" }),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    slug: text("slug").notNull().unique(),
    name: text("name").notNull(),
    status: text("status").notNull().default("draft"),
    branding: jsonb("branding")
      .$type<TrustCenterBranding>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    saSettings: jsonb("sa_settings")
      .$type<TrustCenterSaSettings>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    pausedAt: timestamp("paused_at", { withTimezone: true }),
    currentVersionId: text("current_version_id"),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byPartner: uniqueIndex("partner_trust_centers_partner_unique").on(
      t.partnerId,
    ),
    byTenant: index("partner_trust_centers_tenant_idx").on(t.tenantId),
    bySlug: uniqueIndex("partner_trust_centers_slug_unique").on(t.slug),
    byStatus: index("partner_trust_centers_status_idx").on(t.status),
  }),
);

export const partnerTrustCenterBlocks = pgTable(
  "partner_trust_center_blocks",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    trustCenterId: text("trust_center_id")
      .notNull()
      .references(() => partnerTrustCenters.id, { onDelete: "cascade" }),
    blockType: text("block_type").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    visible: boolean("visible").notNull().default(true),
    content: jsonb("content")
      .$type<TrustCenterBlockContent>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTrustCenter: index("partner_tc_blocks_center_idx").on(
      t.trustCenterId,
      t.sortOrder,
    ),
    uniqBlockType: uniqueIndex("partner_tc_blocks_type_unique").on(
      t.trustCenterId,
      t.blockType,
    ),
  }),
);

export const partnerTrustCenterVersions = pgTable(
  "partner_trust_center_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    trustCenterId: text("trust_center_id")
      .notNull()
      .references(() => partnerTrustCenters.id, { onDelete: "cascade" }),
    versionNumber: integer("version_number").notNull().default(1),
    snapshot: jsonb("snapshot")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    publishedByUserId: text("published_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    publishedAt: timestamp("published_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTrustCenter: index("partner_tc_versions_center_idx").on(
      t.trustCenterId,
      t.versionNumber,
    ),
  }),
);

export const partnerTrustCenterViews = pgTable(
  "partner_trust_center_views",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    trustCenterId: text("trust_center_id")
      .notNull()
      .references(() => partnerTrustCenters.id, { onDelete: "cascade" }),
    ipHash: text("ip_hash"),
    userAgentHash: text("user_agent_hash"),
    referrer: text("referrer"),
    blockEngagement: jsonb("block_engagement")
      .$type<Record<string, number>>()
      .default(sql`'{}'::jsonb`),
    ctaClicked: boolean("cta_clicked").notNull().default(false),
    downloadClicked: boolean("download_clicked").notNull().default(false),
    occurredAt: timestamp("occurred_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    byTrustCenter: index("partner_tc_views_center_idx").on(
      t.trustCenterId,
      t.occurredAt,
    ),
  }),
);

export const insertPartnerTrustCenterSchema = createInsertSchema(
  partnerTrustCenters,
  {
    branding: trustCenterBrandingSchema,
    saSettings: trustCenterSaSettingsSchema,
  },
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPartnerTrustCenter = z.infer<
  typeof insertPartnerTrustCenterSchema
>;
export type PartnerTrustCenter = typeof partnerTrustCenters.$inferSelect;

export const insertPartnerTrustCenterBlockSchema = createInsertSchema(
  partnerTrustCenterBlocks,
  { content: trustCenterBlockContentSchema },
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPartnerTrustCenterBlock = z.infer<
  typeof insertPartnerTrustCenterBlockSchema
>;
export type PartnerTrustCenterBlock =
  typeof partnerTrustCenterBlocks.$inferSelect;

export const insertPartnerTrustCenterVersionSchema = createInsertSchema(
  partnerTrustCenterVersions,
).omit({
  id: true,
  publishedAt: true,
});
export type InsertPartnerTrustCenterVersion = z.infer<
  typeof insertPartnerTrustCenterVersionSchema
>;
export type PartnerTrustCenterVersion =
  typeof partnerTrustCenterVersions.$inferSelect;

export const insertPartnerTrustCenterViewSchema = createInsertSchema(
  partnerTrustCenterViews,
).omit({
  id: true,
  occurredAt: true,
});
export type InsertPartnerTrustCenterView = z.infer<
  typeof insertPartnerTrustCenterViewSchema
>;
export type PartnerTrustCenterView =
  typeof partnerTrustCenterViews.$inferSelect;
